/**
 * Progression d'un élève sur les exercices qu'il refait — pour le prof.
 *
 * On regroupe les tentatives TERMINÉES (avec un `score`) par exercice, on ne
 * garde que ceux refaits au moins `REPEAT_THRESHOLD` fois (« fait souvent »), et
 * on expose une série chronologique de scores + une tendance. Le rendu (SVG) est
 * séparé ; ce module est PUR et testé (`student-progress.test.ts`).
 */

/** Ligne `attempts` (jointe au titre de l'exercice) telle que lue côté hook. */
export interface AttemptRow {
  id: string;
  exercise_id: string | null;
  score: number | null;
  status: string;
  started_at: string;
  completed_at: string | null;
  exercise_title: string | null;
}

export interface ProgressPoint {
  attemptId: string;
  score: number; // 0..100
  at: string; // ISO (completed_at ?? started_at)
}

export type ProgressTrend = 'up' | 'down' | 'flat';

export interface ExerciseProgress {
  exerciseId: string;
  exerciseTitle: string;
  points: ProgressPoint[]; // chronologique, du plus ancien au plus récent
  attemptCount: number;
  firstScore: number;
  lastScore: number;
  bestScore: number;
  avgScore: number;
  delta: number; // lastScore - firstScore
  trend: ProgressTrend;
}

/** Nombre de tentatives à partir duquel un exercice est « fait souvent ». */
export const REPEAT_THRESHOLD = 2;

/** Bande morte (en points de %) au-delà de laquelle une variation est « nette ». */
export const TREND_FLAT_BAND = 5;

export function computeTrend(points: ProgressPoint[], flatBand = TREND_FLAT_BAND): ProgressTrend {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last || points.length < 2) return 'flat';
  const delta = last.score - first.score;
  if (delta > flatBand) return 'up';
  if (delta < -flatBand) return 'down';
  return 'flat';
}

function lastAtMs(e: ExerciseProgress): number {
  const last = e.points[e.points.length - 1];
  return last ? new Date(last.at).getTime() : 0;
}

/**
 * Regroupe les tentatives terminées par exercice et calcule la progression.
 * Tri : les plus refaits d'abord, puis l'activité la plus récente.
 */
export function buildExerciseProgress(rows: AttemptRow[]): ExerciseProgress[] {
  const byExercise = new Map<string, { title: string; points: ProgressPoint[] }>();

  for (const r of rows) {
    if (!r.exercise_id) continue;
    if (r.status !== 'completed') continue;
    if (r.score == null) continue;
    const entry = byExercise.get(r.exercise_id) ?? { title: '', points: [] };
    entry.points.push({ attemptId: r.id, score: r.score, at: r.completed_at ?? r.started_at });
    if (r.exercise_title) entry.title = r.exercise_title;
    byExercise.set(r.exercise_id, entry);
  }

  const out: ExerciseProgress[] = [];
  for (const [exerciseId, { title, points }] of byExercise) {
    if (points.length < REPEAT_THRESHOLD) continue;
    const sorted = [...points].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
    const scores = sorted.map((p) => p.score);
    const firstScore = scores[0] ?? 0;
    const lastScore = scores[scores.length - 1] ?? 0;
    out.push({
      exerciseId,
      exerciseTitle: title || '(exercice supprimé)',
      points: sorted,
      attemptCount: sorted.length,
      firstScore,
      lastScore,
      bestScore: Math.max(...scores),
      avgScore: Math.round(scores.reduce((s, v) => s + v, 0) / scores.length),
      delta: lastScore - firstScore,
      trend: computeTrend(sorted),
    });
  }

  out.sort((a, b) => b.attemptCount - a.attemptCount || lastAtMs(b) - lastAtMs(a));
  return out;
}

/**
 * Coordonnées d'une polyline SVG (x croissant réparti, y = score inversé) sur
 * une aire `width × height` avec une marge `pad`. L'axe Y est fixé 0..100 pour
 * comparer les exercices entre eux. Un seul point → centré horizontalement.
 */
export function sparklinePoints(
  scores: number[],
  width: number,
  height: number,
  pad = 4,
): Array<{ x: number; y: number }> {
  if (scores.length === 0) return [];
  const innerW = Math.max(1, width - pad * 2);
  const innerH = Math.max(1, height - pad * 2);
  const n = scores.length;
  return scores.map((s, i) => {
    const t = n === 1 ? 0.5 : i / (n - 1);
    const clamped = Math.max(0, Math.min(100, s));
    return { x: pad + t * innerW, y: pad + (1 - clamped / 100) * innerH };
  });
}
